import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
    faChevronDown,
    faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "@ts-digital/vrc/es/components/modal";
import classNames from "classnames";
import {
    filter,
    find,
    flatten,
    includes,
    intersectionBy,
    isEmpty,
} from "lodash";
import numbro from "numbro";
import { useState } from "react";
import { FormattedDate, FormattedNumber } from "react-intl";
import { PaymentPlanType } from "../../../PaymentContainerClient/commons/enums";
import PaymentRequestDto from "../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import getFailedPaymentsWithDetailedInstallments from "../../commons/getFailedPaymentsWithDetailedInstallments";
import getPaymentPlansMeaningfullForErrors from "../../commons/getPaymentPlansMeaningfullForErrors";
import { CustomButton } from "../CustomButton";
import "./style.css";

const fm = getFormattedMessageWithScope("components.PaymentFailedModal");

interface PaymentFailedModalProps {
    visible: boolean;
    onClose: Function;
    paymentRequest: PaymentRequestDto;
}

export const PaymentFailedModal = ({
    visible,
    onClose,
    paymentRequest,
}: PaymentFailedModalProps) => {
    const [openedAccordions, setOpenedAccordions] = useState([0]);

    function toogleOpenedAccordions(index: number) {
        if (includes(openedAccordions, index)) {
            setOpenedAccordions(
                filter(
                    openedAccordions,
                    (openedAccordion) => openedAccordion !== index
                )
            );
        } else {
            setOpenedAccordions([...openedAccordions, index]);
        }
    }

    const paymentPlansMeaniningfullForErrors =
        getPaymentPlansMeaningfullForErrors(
            paymentRequest.availablePaymentPlans,
            paymentRequest.payments
        );

    const failedPaymentsInstallments =
        getFailedPaymentsWithDetailedInstallments(
            flatten(
                paymentPlansMeaniningfullForErrors.map(
                    (paymentPlan) => paymentPlan.installments
                )
            ),
            paymentRequest.payments
        );

    return (
        <Modal
            className="c-PaymentFailedModal"
            type="danger"
            visible={visible}
            onClose={onClose}
            closable={false}
            outsideClosable={false}
            title={fm("title")}
            footer={
                <div className="c-PaymentFailedModal-footer">
                    <div className="c-PaymentFailedModal-footer-buttons">
                        <CustomButton
                            kind="primary"
                            onClick={onClose}
                            colorScheme="alert"
                        >
                            {fm("confirm")}
                        </CustomButton>
                    </div>
                </div>
            }
        >
            <div className="c-PaymentFailedModal-content">
                <p className="c-PaymentFailedModal-content-text1">
                    {fm("line1")}
                </p>
                <p className="c-PaymentFailedModal-content-text2">
                    {fm("line2")}
                </p>
                {failedPaymentsInstallments.map((failedPayment, index) => {
                    const isAccordionOpened = includes(openedAccordions, index);
                    const singleInstallmentPaymentPlan = find(
                        paymentRequest.availablePaymentPlans,
                        { type: PaymentPlanType.SingleInstallment }
                    );
                    const isSingleInstallmentPaymentPlan = !isEmpty(
                        intersectionBy(
                            failedPayment.installments,
                            singleInstallmentPaymentPlan?.installments || [],
                            "id"
                        )
                    );

                    return (
                        <div
                            className={classNames(
                                "c-PaymentFailedModal-content-accordion",
                                isAccordionOpened
                                    ? "c-PaymentFailedModal-content-accordion-opened"
                                    : undefined
                            )}
                            key={index}
                        >
                            <div className="c-PaymentFailedModal-content-accordion-header">
                                <div>
                                    <FontAwesomeIcon
                                        icon={
                                            (isAccordionOpened
                                                ? faChevronDown
                                                : faChevronRight) as IconProp
                                        }
                                        className="c-PaymentFailedModal-content-accordion-header-icon"
                                        onClick={() =>
                                            toogleOpenedAccordions(index)
                                        }
                                    />
                                </div>
                                <div className="c-PaymentFailedModal-content-accordion-header-title">
                                    {fm({
                                        id: "error",
                                        values: { number: index + 1 },
                                    })}
                                </div>
                                <div className="c-PaymentFailedModal-content-accordion-header-date">
                                    <FormattedDate
                                        value={failedPayment.paymentCreatedAt}
                                        year="numeric"
                                        month="2-digit"
                                        day="2-digit"
                                    />
                                </div>
                            </div>
                            {isAccordionOpened ? (
                                <div className="c-PaymentFailedModal-content-accordion-content">
                                    <div></div>
                                    <div className="c-PaymentFailedModal-content-accordion-content-error-content">
                                        <div className="c-PaymentFailedModal-content-accordion-content-error-message">
                                            {failedPayment.error}
                                        </div>
                                        <div className="c-PaymentFailedModal-content-accordion-content-error-info">
                                            {failedPayment.installments.map(
                                                (installment, index) => (
                                                    <div
                                                        className="c-PaymentFailedModal-content-accordion-content-error-info-installment"
                                                        key={index}
                                                    >
                                                        <div className="c-PaymentFailedModal-content-accordion-content-error-info-installment-title">
                                                            {fm(
                                                                isSingleInstallmentPaymentPlan
                                                                    ? "total"
                                                                    : "installment"
                                                            )}
                                                        </div>
                                                        <div className="c-PaymentFailedModal-content-accordion-content-error-info-installment-date">
                                                            <FormattedNumber
                                                                style="currency"
                                                                currency={
                                                                    installment
                                                                        .amount
                                                                        .currency
                                                                }
                                                                value={numbro(
                                                                    installment
                                                                        .amount
                                                                        .amount
                                                                ).value()}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : undefined}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};
